import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { CalendarDaysIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import { echeancierService } from '../../api/services';
import { Echeancier } from '../../types';
import { formatTND, formatDate, formatDateISO } from '../../utils/formatters';
import { StatusBadge } from '../common/StatusBadge';

interface Props {
  documentId: number;
  /** Recharge le document parent après modification (statut/échéancier). */
  onChanged?: () => void;
  className?: string;
}

const EcheancierSection: React.FC<Props> = ({ documentId, onChanged, className }) => {
  const [plan, setPlan] = useState<Echeancier | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [nombre, setNombre] = useState(3);
  const [premiere, setPremiere] = useState(formatDateISO(new Date()));
  const [intervalle, setIntervalle] = useState(30);

  const load = () =>
    echeancierService
      .get(documentId)
      .then(setPlan)
      .catch((e) => toast.error(e?.response?.data?.message || 'Erreur chargement échéancier'))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId]);

  const generate = async () => {
    if (nombre < 2) {
      toast.error('Le plan doit comporter au moins 2 échéances.');
      return;
    }
    setBusy(true);
    try {
      await echeancierService.generate(documentId, {
        nombre,
        premiereEcheance: premiere,
        intervalleJours: intervalle,
      });
      toast.success('Échéancier généré');
      await load();
      onChanged?.();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erreur génération');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('Supprimer cet échéancier ? Le suivi global du paiement est conservé.')) return;
    setBusy(true);
    try {
      await echeancierService.delete(documentId);
      toast.success('Échéancier supprimé');
      await load();
      onChanged?.();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erreur suppression');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return null;

  const hasPlan = plan && plan.echeances.length > 0;

  return (
    <div className={`bg-white rounded-xl border border-gray-200 p-4 ${className || ''}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="flex items-center gap-2 font-semibold text-gray-800">
          <CalendarDaysIcon className="w-5 h-5 text-primary-600" />
          Échéancier de paiement
        </h3>
        {hasPlan && (
          <button
            onClick={remove}
            disabled={busy}
            className="text-sm text-red-600 hover:text-red-800 flex items-center gap-1 disabled:opacity-50"
          >
            <TrashIcon className="w-4 h-4" /> Supprimer le plan
          </button>
        )}
      </div>

      {!hasPlan && (
        <div className="space-y-3">
          <p className="text-sm text-gray-500">
            Aucun échéancier. Générez un règlement en plusieurs fois (le net à payer est réparti
            à parts égales).
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-sm">
              <span className="block text-gray-600 mb-1">Nombre d'échéances</span>
              <input
                type="number"
                min={2}
                value={nombre}
                onChange={(e) => setNombre(Number(e.target.value))}
                className="border border-gray-300 rounded px-2 py-1 w-24"
              />
            </label>
            <label className="text-sm">
              <span className="block text-gray-600 mb-1">Première échéance</span>
              <input
                type="date"
                value={premiere}
                onChange={(e) => setPremiere(e.target.value)}
                className="border border-gray-300 rounded px-2 py-1"
              />
            </label>
            <label className="text-sm">
              <span className="block text-gray-600 mb-1">Intervalle (jours)</span>
              <input
                type="number"
                min={1}
                value={intervalle}
                onChange={(e) => setIntervalle(Number(e.target.value))}
                className="border border-gray-300 rounded px-2 py-1 w-24"
              />
            </label>
            <button
              onClick={generate}
              disabled={busy}
              className="btn-primary flex items-center gap-1 disabled:opacity-50"
            >
              <PlusIcon className="w-4 h-4" /> Générer
            </button>
          </div>
        </div>
      )}

      {hasPlan && plan && (
        <>
          {plan.enRetard && (
            <div className="mb-3 text-sm bg-red-50 text-red-700 border border-red-200 rounded px-3 py-2">
              Au moins une échéance est en retard.
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-2 pr-3">#</th>
                  <th className="py-2 pr-3">Libellé</th>
                  <th className="py-2 pr-3">Échéance</th>
                  <th className="py-2 pr-3 text-right">Montant</th>
                  <th className="py-2 pr-3 text-right">Payé</th>
                  <th className="py-2 pr-3 text-right">Reste</th>
                  <th className="py-2">Statut</th>
                </tr>
              </thead>
              <tbody>
                {plan.echeances.map((e) => (
                  <tr key={e.id} className="border-b last:border-0">
                    <td className="py-2 pr-3">{e.ordre}</td>
                    <td className="py-2 pr-3">{e.libelle || '—'}</td>
                    <td className="py-2 pr-3">{formatDate(e.dateEcheance)}</td>
                    <td className="py-2 pr-3 text-right">{formatTND(e.montant)}</td>
                    <td className="py-2 pr-3 text-right">{formatTND(e.montantPaye)}</td>
                    <td className="py-2 pr-3 text-right">{formatTND(e.reste)}</td>
                    <td className="py-2">
                      <StatusBadge statut={e.statut} />
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="font-semibold border-t">
                  <td className="py-2 pr-3" colSpan={3}>
                    Total
                  </td>
                  <td className="py-2 pr-3 text-right">{formatTND(plan.totalEcheances)}</td>
                  <td className="py-2 pr-3 text-right">{formatTND(plan.totalPaye)}</td>
                  <td className="py-2 pr-3 text-right">{formatTND(plan.resteGlobal)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
          {plan.prochaineEcheance && (
            <p className="mt-2 text-sm text-gray-500">
              Prochaine échéance : {formatDate(plan.prochaineEcheance)}
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default EcheancierSection;
